import type { ReactNode } from 'react'
import AppHeader from '../../components/shell/AppHeader'
import EmptyState from '../../components/shell/EmptyState'
import { formatScheduleLabel, useTimeFormat } from '../../lib/timeFormat'
import { PAY_PERIOD } from '../agent/payPeriod19'

/**
 * Placeholder content standing in for the real coverage, personnel, and
 * report data, until the schema and the coverage calculation behind this
 * screen exist (see docs/product/delivery-order.md). The shape matches
 * docs/product/personas.md's leadership scope: every team, read-only.
 */
// A placeholder for whoever is signed in: once the profile carries a display
// name, this comes from the signed-in leadership account's own record rather
// than being fixed here.
const LEADERSHIP = { initials: 'JA' }

type TeamManpower = { name: string; available: number; onLeave: number; detailedOut: number; understaffed: number }

// docs/product/synthetic-data.md defines the whole pilot as approximately
// 35 to 40 agents across several teams; these three sum to 32 (available
// plus on leave plus detailed out), leaving room for a regular day off
// accounting for the rest on any given day.
const TEAMS: TeamManpower[] = [
  { name: 'Team 1', available: 9, onLeave: 1, detailedOut: 1, understaffed: 1 },
  { name: 'Team 2', available: 8, onLeave: 2, detailedOut: 1, understaffed: 2 },
  { name: 'Team 3', available: 8, onLeave: 1, detailedOut: 1, understaffed: 1 },
]

const ALL_TEAMS: TeamManpower = TEAMS.reduce(
  (totals, team) => ({
    name: 'All teams',
    available: totals.available + team.available,
    onLeave: totals.onLeave + team.onLeave,
    detailedOut: totals.detailedOut + team.detailedOut,
    understaffed: totals.understaffed + team.understaffed,
  }),
  { name: 'All teams', available: 0, onLeave: 0, detailedOut: 0, understaffed: 0 },
)

function RepeatIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 2.1l4 4-4 4" />
      <path d="M3 12.7V11a4 4 0 0 1 4-4h14" />
      <path d="M7 21.9l-4-4 4-4" />
      <path d="M21 11.3V13a4 4 0 0 1-4 4H3" />
    </svg>
  )
}

function TriangleAlertIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    </svg>
  )
}

function ClockIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  )
}

// The same gap the supervisor dashboard shows for Team 3, plus one from
// another team: docs/product/personas.md requires leadership to see the
// supervisor dashboard's coverage summary across every team, not only
// conflicts and manpower.
const COVERAGE_GAPS: { title: string; detail: string }[] = [
  { title: 'Sep 18 · 0600–1400 · Team 3', detail: 'Case Processing · 4 of 6 required' },
  { title: 'Sep 19 · 1400–2200 · Team 2', detail: 'Intake · 3 of 5 required' },
]

// The same conflict the supervisor dashboard shows for Team 3, since this
// is a read-only, org-wide view of the same underlying pilot: leadership
// sees it too, alongside every other team's.
const CONFLICTS: { title: string; detail: string }[] = [
  { title: 'Schedule conflict · Ofc. T. Brooks · Team 3', detail: 'Overlapping shifts, Sep 17' },
]

const SIGNALS: { title: string; detail: string }[] = [
  {
    title: 'Recurring absence pattern · Ofc. R. Chen · Team 3',
    detail: 'Fridays, 3 of the last 6 pay periods: Jul 31, Aug 14, Aug 28',
  },
  {
    title: 'Recurring absence pattern · Ofc. M. Alvarez · Team 1',
    detail: 'Mondays, 3 of the last 6 pay periods: Aug 3, Aug 17, Aug 31',
  },
]

const REPORTS = [
  'Daily coverage',
  'Two-week manpower',
  'Leave & availability',
  'Detailed out',
  'Daily assignment sheet',
  'Seniority roster',
  'Overtime planning',
]

function SectionTitle({ children }: { children: string }) {
  return <h2 className="text-body font-extrabold">{children}</h2>
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-surface border border-line bg-canvas">
      <div className="border-b border-line px-4 py-3">
        <SectionTitle>{title}</SectionTitle>
      </div>
      {children}
    </div>
  )
}

function ManpowerMetrics({ team }: { team: TeamManpower }) {
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:hidden">
      <div>
        <p className="text-caption text-ink-subtle">Available</p>
        <p className="text-small font-bold [font-variant-numeric:tabular-nums]">{team.available}</p>
      </div>
      <div>
        <p className="text-caption text-ink-subtle">On leave</p>
        <p className="text-small font-bold [font-variant-numeric:tabular-nums]">{team.onLeave}</p>
      </div>
      <div>
        <p className="text-caption text-ink-subtle">Detailed out</p>
        <p className="text-small font-bold [font-variant-numeric:tabular-nums]">{team.detailedOut}</p>
      </div>
      <div>
        <p className="text-caption text-ink-subtle">Understaffed</p>
        <p className="text-small font-bold text-error [font-variant-numeric:tabular-nums]">{team.understaffed}</p>
      </div>
    </div>
  )
}

export default function LeadershipDashboardPage() {
  const { format } = useTimeFormat()
  return (
    <div className="flex h-dvh flex-col bg-surface text-ink">
      <a
        href="#leadership-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-canvas focus:px-4 focus:py-2 focus:font-semibold"
      >
        Skip to main content
      </a>

      <AppHeader initials={LEADERSHIP.initials} />

      <main id="leadership-main" tabIndex={0} className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-5 lg:gap-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-baseline sm:justify-between">
            <div>
              <h1 className="text-h2 font-extrabold">Leadership Overview</h1>
              <p className="mt-1 text-small text-ink-muted">
                Orlando Field Office &middot; {PAY_PERIOD.label}, {PAY_PERIOD.range}
              </p>
            </div>
            <div className="flex gap-2">
              <span className="rounded-control border border-line-strong px-3.5 py-2 text-small font-bold text-ink-muted">
                Personnel directory
              </span>
              <span className="rounded-control border border-line-strong px-3.5 py-2 text-small font-bold text-ink-muted">
                Published schedules
              </span>
            </div>
          </div>

          {/* Team manpower: a grid table at sm and up, one card per team below
              it. A five-column grid has no room to breathe at a phone width,
              so mobile gets the same numbers as label-value pairs instead of
              a squeezed row. */}
          <div className="rounded-surface border border-line bg-canvas">
            {/* A real table, not a styled grid: this is tabular data, and a
                screen reader needs the row and column-header associations a
                table gives it for free. */}
            <div className="hidden overflow-x-auto sm:block">
              <table className="w-full border-collapse">
                <caption className="sr-only">Manpower by team, {PAY_PERIOD.label}</caption>
                <thead>
                  <tr className="border-b border-line bg-surface-2">
                    <th scope="col" className="px-4 py-2.5 text-left text-caption font-bold tracking-wide text-ink-subtle uppercase">
                      Team
                    </th>
                    <th scope="col" className="px-4 py-2.5 text-right text-caption font-bold tracking-wide text-ink-subtle uppercase">
                      Available
                    </th>
                    <th scope="col" className="px-4 py-2.5 text-right text-caption font-bold tracking-wide text-ink-subtle uppercase">
                      On leave
                    </th>
                    <th scope="col" className="px-4 py-2.5 text-right text-caption font-bold tracking-wide text-ink-subtle uppercase">
                      Detailed out
                    </th>
                    <th scope="col" className="px-4 py-2.5 text-right text-caption font-bold tracking-wide text-ink-subtle uppercase">
                      Understaffed
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {TEAMS.map((team) => (
                    <tr key={team.name} className="border-b border-line last:border-b-0">
                      <th scope="row" className="px-4 py-3 text-left text-small font-bold">
                        {team.name}
                      </th>
                      <td className="px-4 py-3 text-right text-small font-bold [font-variant-numeric:tabular-nums]">
                        {team.available}
                      </td>
                      <td className="px-4 py-3 text-right text-small font-bold [font-variant-numeric:tabular-nums]">
                        {team.onLeave}
                      </td>
                      <td className="px-4 py-3 text-right text-small font-bold [font-variant-numeric:tabular-nums]">
                        {team.detailedOut}
                      </td>
                      <td className="px-4 py-3 text-right text-small font-bold text-error [font-variant-numeric:tabular-nums]">
                        {team.understaffed}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-surface-2">
                    <th scope="row" className="px-4 py-3 text-left text-small font-extrabold">
                      {ALL_TEAMS.name}
                    </th>
                    <td className="px-4 py-3 text-right text-small font-extrabold [font-variant-numeric:tabular-nums]">
                      {ALL_TEAMS.available}
                    </td>
                    <td className="px-4 py-3 text-right text-small font-extrabold [font-variant-numeric:tabular-nums]">
                      {ALL_TEAMS.onLeave}
                    </td>
                    <td className="px-4 py-3 text-right text-small font-extrabold [font-variant-numeric:tabular-nums]">
                      {ALL_TEAMS.detailedOut}
                    </td>
                    <td className="px-4 py-3 text-right text-small font-extrabold text-error [font-variant-numeric:tabular-nums]">
                      {ALL_TEAMS.understaffed}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="flex flex-col gap-3 p-4 sm:hidden">
              {TEAMS.map((team) => (
                <div key={team.name} className="rounded-surface border border-line p-3.5">
                  <p className="text-small font-extrabold">{team.name}</p>
                  <div className="mt-2">
                    <ManpowerMetrics team={team} />
                  </div>
                </div>
              ))}
              <div className="rounded-surface border border-line bg-surface-2 p-3.5">
                <p className="text-small font-extrabold">{ALL_TEAMS.name}</p>
                <div className="mt-2">
                  <ManpowerMetrics team={ALL_TEAMS} />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[1.6fr_1fr] lg:gap-6">
            <div className="flex flex-col gap-5">
              <Panel title="Coverage gaps · next 14 days">
                {COVERAGE_GAPS.length === 0 ? (
                  <EmptyState>No coverage gaps on any team in the next 14 days.</EmptyState>
                ) : (
                  <ul>
                    {COVERAGE_GAPS.map((gap) => (
                      <li key={gap.title} className="flex items-center gap-3 border-b border-line px-4 py-3 last:border-b-0">
                        <span className="flex size-8 flex-none items-center justify-center rounded-full bg-error/10 text-error">
                          <ClockIcon />
                        </span>
                        <div className="flex-1">
                          <p className="text-small font-bold">{formatScheduleLabel(gap.title, format)}</p>
                          <p className="mt-0.5 text-caption text-ink-muted">{gap.detail}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>

              <Panel title="Conflicts">
                {CONFLICTS.length === 0 ? (
                  <EmptyState>No schedule conflicts on any team.</EmptyState>
                ) : (
                  <ul>
                    {CONFLICTS.map((conflict) => (
                      <li key={conflict.title} className="flex items-center gap-3 border-b border-line px-4 py-3 last:border-b-0">
                        <span className="flex size-8 flex-none items-center justify-center rounded-full bg-error/10 text-error">
                          <TriangleAlertIcon />
                        </span>
                        <div className="flex-1">
                          <p className="text-small font-bold">{conflict.title}</p>
                          <p className="mt-0.5 text-caption text-ink-muted">{conflict.detail}</p>
                        </div>
                        <span className="inline-flex flex-none items-center gap-1 rounded-full bg-error/10 px-2.5 py-0.5 text-caption font-bold text-error">
                          Blocking
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>

              <Panel title="Recurring absence signals">
                {SIGNALS.length === 0 ? (
                  <EmptyState>No recurring absence signals for any team.</EmptyState>
                ) : (
                  <ul>
                    {SIGNALS.map((signal) => (
                      <li key={signal.title} className="flex items-center gap-3 border-b border-line px-4 py-3 last:border-b-0">
                        <span className="flex size-8 flex-none items-center justify-center rounded-full bg-warning/10 text-warning">
                          <RepeatIcon />
                        </span>
                        <div className="flex-1">
                          <p className="text-small font-bold">{signal.title}</p>
                          <p className="mt-0.5 text-caption text-ink-muted">{signal.detail}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            </div>

            <Panel title="Reports">
              {REPORTS.length === 0 ? (
                <EmptyState>No reports available yet.</EmptyState>
              ) : (
                REPORTS.map((report) => (
                  <div key={report} className="border-b border-line px-4 py-2.5 text-small font-semibold last:border-b-0">
                    {report}
                  </div>
                ))
              )}
            </Panel>
          </div>
        </div>
      </main>
    </div>
  )
}
